import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  apiErrorMessage,
  loginWithPassword,
  registerUser,
  resendVerification,
} from '@/api/client';
import { AuthShell, OAuthButtons } from '@/components/auth/AuthShell';
import { loadLastAuthMethod, storeLastAuthMethod } from '@/lib/auth-prefs';
import { usePageMeta } from '@/hooks/use-page-meta';

type Mode = 'signin' | 'signup';

export function LoginPage() {
  // Canonical strips the ?mode=signup variant so both auth modes resolve to
  // one indexed URL.
  usePageMeta("Sign in — Aura", { description: "Sign in to run practice voice interviews or manage recruiter invites.", canonical: window.location.origin + '/login' })
  const { setAuth, login } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resent, setResent] = useState(false);
  // Read once — the label reflects the previous visit, and a fresh sign-in
  // navigates away before it could need re-reading.
  const [lastAuth] = useState(loadLastAuthMethod);

  const onSuccess = (token: string, user: { id: string; email: string; name: string; role: 'admin' | 'candidate' | 'recruiter' | ''; avatar_url?: string | null }) => {
    setAuth(token, { ...user, avatar_url: user.avatar_url ?? undefined });
    // Same routing as the OAuth callback: deep links survive the round-trip,
    // and users who have not chosen a role go through the picker first.
    const returnTo = sessionStorage.getItem('aura_return_to');
    sessionStorage.removeItem('aura_return_to');
    if (returnTo) {
      navigate(returnTo, { replace: true });
    } else if (user.role === 'admin') {
      navigate('/admin', { replace: true });
    } else if (user.role === 'recruiter') {
      navigate('/recruiter', { replace: true });
    } else if (user.role === '') {
      navigate('/choose-role', { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setNeedsVerification(false);
    setResent(false);
    setBusy(true);
    try {
      if (mode === 'signup') {
        const { message } = await registerUser(email, password, name);
        setNotice(message);
        setMode('signin');
      } else {
        const { token, user } = await loginWithPassword(email, password);
        storeLastAuthMethod('password');
        onSuccess(token, user);
      }
    } catch (err) {
      const msg = apiErrorMessage(err);
      setError(msg);
      const status = (err as { response?: { status?: number } }).response?.status;
      const code = (err as { response?: { data?: { detail?: { code?: string } } } }).response?.data?.detail?.code;
      if (status === 403 && code === 'email_not_verified') {
        setNeedsVerification(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    setError('');
    setBusy(true);
    try {
      const { message } = await resendVerification(email);
      setResent(true);
      setNotice(message);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={mode === 'signin' ? 'Sign in to Aura' : 'Create your account'}
      subtitle={
        mode === 'signin'
          ? 'Practice a real-time AI voice interview.'
          : 'Start with a free AI voice interview.'
      }
      footer={
        mode === 'signin' ? (
          <span>
            New to Aura?{' '}
            <button
              type="button"
              className="auth-link-button"
              onClick={() => {
                setMode('signup');
                setError('');
                setNotice('');
              }}
            >
              Create an account
            </button>
          </span>
        ) : (
          <span>
            Already have an account?{' '}
            <button
              type="button"
              className="auth-link-button"
              onClick={() => {
                setMode('signin');
                setError('');
                setNotice('');
              }}
            >
              Sign in
            </button>
          </span>
        )
      }
    >
      <OAuthButtons
        onProvider={(p) => login(p)}
        lastUsed={lastAuth === 'google' || lastAuth === 'github' ? lastAuth : undefined}
      />

      <div className="auth-divider">or</div>

      {lastAuth === 'password' && mode === 'signin' && (
        <p className="auth-last-hint">You signed in with your email last time.</p>
      )}

      {needsVerification && (
        <div className="auth-banner auth-banner-warn" role="alert">
          <p>{error}</p>
          {!resent ? (
            <button type="button" onClick={onResend} disabled={busy}>
              Resend verification email
            </button>
          ) : (
            <p style={{ marginTop: 8 }}>Verification email sent — check your inbox.</p>
          )}
        </div>
      )}
      {!needsVerification && error && (
        <div className="auth-banner auth-banner-error" role="alert">{error}</div>
      )}
      {notice && !error && (
        <div className="auth-banner auth-banner-success" role="status">{notice}</div>
      )}

      {/* Keyed by mode: the signin/signup swap remounts the form, so the
          Name field and copy crossfade in instead of popping. Controlled
          state lives above, so values survive the remount. */}
      <form onSubmit={onSubmit} className="auth-form aura-panel-in" key={mode}>
        {mode === 'signup' && (
          <label className="auth-field">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ada Lovelace" maxLength={100} />
          </label>
        )}
        <label className="auth-field">
          <span>Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </label>
        <label className="auth-field">
          <span>Password</span>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={mode === 'signup' ? 'At least 8 characters' : 'Your password'}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          />
        </label>
        {mode === 'signin' && (
          <div className="auth-hint">
            <Link to="/forgot-password" className="">
              Forgot password?
            </Link>
          </div>
        )}
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}
        </button>
      </form>
    </AuthShell>
  );
}
